<template>
	<div
		class="pinned-message-container"
		:class="{ 'menu-open': isMenuOpen }"
		role="link"
	>
		<div class="pinned-message">
			<ThumbtackIcon class="pin-icon" halo type="solid" />
			<div class="message-text-container">
				<div
					class="message-text"
					v-text="parentText"
					:class="{ 'link-only': linkOnly }"
				></div>
				<div class="message-text-title" v-if="titleText" v-text="titleText" />
				<PollCloseState v-if="message?.poll_id" :pollId="message?.poll_id" />
			</div>
			<div class="media-container">
				<PhotoVideoThumbnail
					v-if="media?.isPhoto || media?.isVideo"
					class="media"
					:media="media"
				/>
				<FileIcon v-if="media?.isFile" class="file" :icon="media?.fileIcon" />
				<AudioMessageIcon v-if="media?.isAudio" class="audio" />
				<PhotoVideoThumbnail
					v-if="message?.resource?.thumbnailurl && !media"
					class="media"
					:media="resourceMedia"
				/>
				<SquarePollHorizontalIcon
					v-if="message?.poll_id"
					class="poll"
					type="solid"
				/>
			</div>
			<Menu
				@click.stop.prevent
				:items="menuItems"
				bottom
				@select="onSelectMenuItem"
				ref="menu"
				:offsetY="8"
				@open="onMenuOpen"
			>
				<div class="dismiss-pin">
					<CloseIcon type="regular" />
				</div>

				<template #item="{ item }">
					<div class="option-item">
						<div class="option-title-desc">
							<div class="item-label">{{ item.label }}</div>
							<div class="item-desc">{{ item.desc }}</div>
						</div>

						<component class="item-icon" :is="item.icon" type="regular" />
					</div>
				</template>
			</Menu>
			<StandardModal
				v-if="showHideModal"
				open
				:title="t('chat_pin_message_hide_confirmation_title')"
				:confirmText="t('chat_pin_message_hide_action')"
				:confirmProps="{ danger: true, loading: isHiding }"
				@confirm="hideMessage"
				@cancel="showHideModal = false"
				small
				ref="modal"
			>
				<div class="hide-modal-wrapper">
					<ThumbtackIcon halo class="unpin-logo" type="regular" />
					<div class="help">
						{{ t('chat_pin_message_hide_confirmation_description') }}
					</div>
				</div>
			</StandardModal>
		</div>
	</div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import store, { getChat, getMessage, updateChat } from '@/store'
import {
	ThumbtackIcon,
	AudioMessageIcon,
	CloseIcon,
	EyeSlashIcon,
	ThumbtackSlashIcon,
	SquarePollHorizontalIcon,
} from '@/icons'
import {
	PhotoVideoThumbnail,
	Menu,
	useAlert,
	StandardModal,
} from '@/components/common'
import FileIcon from '@/components/common/FileIcon'
import { useI18n } from 'vue-i18n'
import { updateChat as apiUpdateChat } from '@/api/chat'
import { updateMembership } from '@/api/membership'
import { parseMessageParts, partType } from '../Message/utils'
import { mediaTypes } from '@/helpers/constants'
import { useEventBus } from '@/helpers/bus'
import PollCloseState from '@/components/Polls/PollCloseState'
import { getPoll } from '@/store/messages'
import { chatCan, PIN_MESSAGE } from '@/helpers/permissions'

const defaultMediaText = {
	[mediaTypes.PHOTO]: 'chat_pin_message_pinned_photo',
	[mediaTypes.VIDEO]: 'chat_pin_message_pinned_video',
	[mediaTypes.FILE]: 'chat_pin_message_pinned_file',
	[mediaTypes.AUDIO]: 'chat_pin_message_pinned_audio',
}
const props = defineProps(['chatId'])
const { t } = useI18n()
const alert = useAlert()
const isMenuOpen = ref(false)
const showHideModal = ref(false)
const isHiding = ref(false)
const message = ref(null)

useEventBus('message-change', () => {
	message.value = getMessageFromStore()
})

onMounted(() => {
	message.value = getMessageFromStore()
})

const media = computed(() => {
	return message.value?.messagemedia[0] || null
})

const resourceMedia = computed(() => {
	return {
		isPhoto: true,
		mediaUrl: message.value?.resource?.thumbnailurl,
	}
})

const menuItems = computed(() => {
	const items = [
		{
			id: 'hide',
			label: t('chat_pin_message_hide_action'),
			desc: t('chat_pin_message_hide_action_subtitle'),
			icon: EyeSlashIcon,
		},
	]

	// Only add unpin option if user has permission
	if (chatCan(getChat(props.chatId), PIN_MESSAGE)) {
		items.push({
			id: 'unpin',
			label: t('chat_drawer_action_unpin'),
			desc: t('chat_messageaction_unpin_subtitle'),
			icon: ThumbtackSlashIcon,
		})
	}

	return items
})

//redo stuff
const messageParts = computed(() => {
	if (!message.value) return []
	return parseMessageParts(message.value)
})

const linkOnly = computed(() => {
	return (
		showLinkCard.value &&
		!messageParts.value.find((part) => part.type === partType.TEXT)
	)
})

const parentText = computed(() => {
	// Handle poll translations
	const poll = getPoll(message.value?.poll_id) || {}
	if (message.value?.poll_id && poll?.isTranslated && poll?.translations) {
		const me = store.auth.user
		const translation = message.value.poll.translations.find(
			(t) => t.language === me.language
		)
		if (translation) {
			return translation.translation
		}
	}

	// Handle message translations
	if (message.value?.isTranslated && message.value?.messagetrans) {
		return message.value.messagetrans.translation
	}

	// No translations, return original text
	return (
		message.value?.poll?.question ||
		text.value ||
		message.value?.resource?.providername ||
		defaultText.value ||
		null
	)
})

const text = computed(() => {
	return messageParts.value
		.map(({ type, userId, user, text }) => {
			switch (type) {
				case partType.MENTION:
					if (user) return `@${user.fullname}`
					if (userId === 'everyone') return t('model_mentions_moniker_everyone')
					return `@${t('model_user_noname', [userId])}`
				case partType.HYPERLINK:
				case partType.TEXT:
					return text
				default:
					return null
			}
		})
		.filter((part) => part)
		.join('')
})

const defaultText = computed(() => {
	if (!message.value?.hasMedia) return null
	if (message.value?.mediaCount > 1) {
		return t('chat_pin_message_pinned_attachements', {
			Count: message.value.mediaCount,
		})
	}
	const defaultText = defaultMediaText[message.value?.media?.mediatype]
	if (defaultText) {
		return t(defaultText)
	}
	return null
})

const titleText = computed(() => {
	if (showLinkCard.value) return message.value?.resource.title
	if (message.value?.task)
		return t('chat_message_quoted_task', [message.value?.task.title])
	if (media.value?.isFile && message.value.mediaCount === 1)
		return media.value.title
	return null
})

const showLinkCard = computed(() => {
	return (
		!media.value &&
		message.value?.resource &&
		messageParts.value.find((part) => part.type === partType.LINK_CARD)
	)
})

const onSelectMenuItem = ({ id }) => {
	if (id === 'unpin') {
		unpinMessage()
	} else {
		showHideModal.value = true
	}
}

const unpinMessage = async () => {
	try {
		const chatId = message.value?.bubble_id
		updateChat(chatId, { pinned_message_id: null })
		await apiUpdateChat(chatId, {
			pinned_message_id: null,
		})
	} catch (e) {
		updateChat({ pinned_message_id: message.value?.id })
		alert.error(t('chat_pin_message_unpin_failure_title'))
	}
}

const hideMessage = async () => {
	const chatId = message.value?.bubble_id
	isHiding.value = true
	const { ok } = await updateMembership({
		bubble_id: chatId,
		pinned_message_hidden: true,
	})
	isHiding.value = false
	showHideModal.value = false
	if (!ok) {
		alert.error(t('chat_pin_message_hide_failure_title'))
	}
}

const onMenuOpen = (isOpen) => {
	isMenuOpen.value = isOpen
}

const getMessageFromStore = () => {
	const chat = getChat(props.chatId)
	return getMessage(chat?.pinned_message_id)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.pinned-message-container {
	cursor: pointer;
	background-color: rgba(255, 255, 255, 0.8);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(8px);
	position: absolute;
	left: 0;
	right: 0;
	overflow: hidden;
	box-shadow: 0px 2px 6px -6px #bababa40;
	z-index: 2;
	@media screen and (max-width: 800px) {
		left: 0;
	}

	&.menu-open,
	&:hover {
		background-color: rgba(255, 255, 255, 0.9);
		.media-container {
			transform: translateX(-25px);
			margin-right: 0;
		}

		.dismiss-pin {
			opacity: 1;
			transform: translateX(0);
			cursor: pointer;
			margin-left: 16px;
		}
	}
	.pinned-message {
		display: flex;
		align-items: center;
		padding: 15px 15px 15px 10px;
		.message-text-container {
			width: 100%;
			.message-text {
				overflow: hidden;
				text-overflow: ellipsis;
				font-family: Avenir-Roman;
				font-size: 15px;
				color: var(--neutral-800);
				width: calc(100% - 30px) !important;
				display: -webkit-box !important;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical !important;
				overflow: hidden !important;
			}
			.message-text-title {
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: 100%;
				font-family: Avenir-Heavy;
				font-size: 15px;
				color: var(--neutral-800);
				width: 100%;
			}
			.link-only {
				color: var(--blue-400);
			}
		}
		.pin-icon {
			flex: 0 0 auto;
			color: var(--neutral-500);
			font-size: 20px;
			margin-right: 12px;
			&::before {
				border-radius: 8px;
			}
		}
		.media-container {
			margin-left: auto;
			margin-right: -25px;
			transition: transform 0.3s ease;
			width: 36px;
			height: 36px;
			.media,
			.file,
			.audio {
				font-size: 36px;
			}
			.audio {
				color: var(--teal-300);
			}
			.poll {
				font-size: 30px;
				color: var(--teal-300);
			}
		}
		.poll-close-label {
			font-size: 14px;
			margin-top: 6px;
		}
	}
	.dismiss-pin {
		display: flex;
		background-color: var(--neutral-100);
		border-radius: 50%;
		padding: 5px;
		margin-left: 0;
		opacity: 0;
		transition: opacity 0.3s ease, transform 0.3s ease, margin-left 0.3s ease;
		transform: translateX(24px);
		height: 24px;
		width: 24px;
		align-items: center;
		justify-content: center;
		:deep(.popover-trigger) {
			display: flex;
		}
		.pronto-icon {
			font-size: 12px;
			color: var(--neutral-500);
		}
		&:hover {
			background-color: var(--neutral-200);
		}
		&:active {
			background-color: var(--neutral-300);
		}
	}
}
.option-item {
	display: flex;
	align-items: center;
	width: 100%;
	gap: 10px;
	.item-desc {
		font-size: 12px;
	}
	.item-icon {
		font-size: 16px;
		margin-left: auto;
		margin-right: 5px;
		width: 25px;
		:deep(svg) {
			width: 25px;
		}
	}
	.item-icon:nth-of-type(2) {
		:deep(svg) {
			margin-left: -3px;
		}
	}
}

.menu-section .menu-item:nth-of-type(2) .option-item .item-icon {
	:deep(svg) {
		margin-left: -3px;
	}
}

.hide-modal-wrapper {
	@include flex-column-center;
	.unpin-logo {
		color: var(--red-200);
		font-size: 50px;
	}

	.unpin-logo,
	.help {
		margin-top: 24px;
	}

	.help {
		@include fontFamily();
		color: var(--neutral-800);
		max-width: 400px;
		font-size: 16px;
		text-align: center;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.pinned-message-container {
		background-color: rgba(31, 31, 31, 0.8);
		box-shadow: 0px 2px 6px -6px #1e1e1ec5;
		&:hover,
		&.menu-open {
			background-color: rgba(31, 31, 31, 0.9);
		}
		.dismiss-pin {
			background-color: var(--neutral-500);
			&:hover {
				background-color: var(--neutral-400);
			}
			.pronto-icon {
				color: var(--neutral-0);
			}
		}
	}
}
</style>
